import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, TrendingUp, TrendingDown, Users, AlertTriangle, Zap, Target,
  Loader2, ArrowUpRight, ArrowDownRight, Award, Sparkles, Calendar,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { DateRangePills, formatDaysLabel } from './AnalyticsFilters';
import { Card, CardContent } from '../ui/card';
import ChaseKingsCard from './ChaseKingsCard';
import FrequentCollaboratorsCard from './FrequentCollaboratorsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LENSES = [
  { id: 'score',     label: 'Score',     icon: Target,     help: 'Average grading score' },
  { id: 'volume',    label: 'Volume',    icon: Activity,   help: 'Total calls handled' },
  { id: 'revival',   label: 'Revival',   icon: Sparkles,   help: 'Revivable deals touched' },
  { id: 'discovery', label: 'Discovery', icon: TrendingUp, help: 'Calls scoring 90+' },
  { id: 'kudos',     label: 'Kudos',     icon: Award,      help: 'Kudos received' },
];

const SEV_STYLE = {
  high:   { bg: 'bg-rose-50',   border: 'border-rose-200',   text: 'text-rose-700',   iconBg: 'bg-rose-500' },
  medium: { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  iconBg: 'bg-amber-500' },
  low:    { bg: 'bg-slate-50',  border: 'border-slate-200',  text: 'text-slate-700',  iconBg: 'bg-slate-500' },
};

// ── KPI card ────────────────────────────────────────────────────────────────
const Kpi = ({ label, value, delta, deltaSuffix = '%', testid, help }) => {
  const isUp = typeof delta === 'number' && delta > 0;
  const isDown = typeof delta === 'number' && delta < 0;
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={testid}>
      <CardContent className="pt-5">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <p data-kpi-value className="text-3xl font-bold text-slate-900 tabular-nums mt-1">{value ?? '—'}</p>
        {typeof delta === 'number' ? (
          <p className={`text-xs font-semibold mt-1 inline-flex items-center gap-1 ${
            isUp ? 'text-emerald-600' : isDown ? 'text-rose-600' : 'text-slate-500'
          }`}>
            {isUp && <ArrowUpRight className="w-3.5 h-3.5" />}
            {isDown && <ArrowDownRight className="w-3.5 h-3.5" />}
            {delta > 0 ? `+${delta}${deltaSuffix}` : `${delta}${deltaSuffix}`}
            <span className="text-slate-400 font-normal ml-0.5">vs prev</span>
          </p>
        ) : (
          <p className="text-xs text-slate-400 mt-1">{help || 'no prior data'}</p>
        )}
      </CardContent>
    </Card>
  );
};

// ── Leaderboard ─────────────────────────────────────────────────────────────
// Intent tabs — "All" plus the three call_intent categories so managers
// can crown a "Follow-Up Champion" beside the existing top scorer.
const INTENT_TABS = [
  { id: null,             label: 'All Intents',     accent: 'teal',  emoji: '🏆' },
  { id: 'initial_sales',  label: 'Initial Sales',   accent: 'sky',   emoji: '📞' },
  { id: 'follow_up',      label: 'Follow-Up',       accent: 'amber', emoji: '🔁' },
  { id: 'non_sales',      label: 'Non-Sales',       accent: 'slate', emoji: '🛠' },
];
const INTENT_HEADLINE = {
  null: 'Agent Leaderboard',
  initial_sales: 'Initial Sales Leaderboard',
  follow_up: 'Follow-Up Champion',
  non_sales: 'Non-Sales Hero',
};
const INTENT_ACTIVE = {
  teal:  'bg-teal-600 text-white',
  sky:   'bg-sky-600 text-white',
  amber: 'bg-amber-600 text-white',
  slate: 'bg-slate-700 text-white',
};

const Leaderboard = ({ domainId }) => {
  const { token } = useAuth();
  const [lens, setLens] = useState('score');
  const [intent, setIntent] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    p.set('lens', lens);
    p.set('days', '30');
    p.set('limit', '10');
    if (intent) p.set('intent', intent);
    axios.get(`${API}/manager/leaderboard?${p.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [lens, intent, domainId, token]);

  const headline = INTENT_HEADLINE[intent] || 'Agent Leaderboard';
  const intentSuffix = intent
    ? ` · intent: ${intent.replace('_', ' ')}`
    : '';

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="manager-leaderboard">
      <CardContent className="pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-slate-900 font-bold text-sm">{headline}</h3>
            <p className="text-[11px] text-slate-500">
              Ranked by <span className="font-semibold">{LENSES.find(l => l.id === lens)?.help}</span>
              {' '}· last 30 days{intentSuffix}
            </p>
          </div>
          <div className="flex gap-1 flex-wrap" data-testid="leaderboard-lens-picker">
            {LENSES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setLens(id)}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors ${
                  lens === id ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`lens-${id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Intent tabs — one row of pill toggles above the ranking */}
        <div
          className="flex flex-wrap gap-1.5 mb-4 pb-3 border-b border-slate-100"
          data-testid="leaderboard-intent-picker"
        >
          {INTENT_TABS.map(({ id, label, accent, emoji }) => {
            const active = intent === id;
            const key = id === null ? 'all' : id;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setIntent(id)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-colors ${
                  active
                    ? INTENT_ACTIVE[accent]
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`intent-tab-${key}`}
              >
                <span aria-hidden>{emoji}</span>
                {label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
        ) : !data?.rows?.length ? (
          <p className="text-center text-slate-400 text-sm py-6" data-testid="leaderboard-empty">
            {intent
              ? `No graded ${intent.replace('_', ' ')} calls in the last 30 days. Try backfilling call intent from Admin → Ops Controls, then check again.`
              : 'No agents match this lens yet.'}
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.rows.map(r => (
              <div key={r.agent_id} className="flex items-center gap-3 py-2.5" data-testid={`leaderboard-row-${r.agent_id}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border shrink-0
                  ${r.rank === 1 ? 'bg-amber-100 text-amber-700 border-amber-200'
                  : r.rank === 2 ? 'bg-slate-100 text-slate-700 border-slate-200'
                  : r.rank === 3 ? 'bg-orange-100 text-orange-700 border-orange-200'
                  : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                  {r.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{r.agent_name}</p>
                  <p className="text-[11px] text-slate-500">
                    {r.volume} calls · {r.graded} graded
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900 tabular-nums">
                    {r[lens] ?? '—'}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{LENSES.find(l => l.id === lens)?.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Forecast ────────────────────────────────────────────────────────────────
const ForecastCard = ({ forecast }) => {
  if (!forecast) return null;
  const bars = forecast.per_day || [];
  const maxCalls = Math.max(1, ...bars.map(b => b.projected_calls || 0));
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="manager-forecast">
      <CardContent className="pt-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-slate-900 font-bold text-sm flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-violet-500" /> 7-Day Forecast
            </h3>
            <p className="text-[11px] text-slate-500">Trailing {forecast.based_on_days}-day average · updated live</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-slate-900 tabular-nums">{forecast.projected_total_calls}</p>
            <p className="text-[11px] text-slate-500">calls expected · {forecast.projected_total_revivable_deals} revivable</p>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5 mt-4">
          {bars.map(b => {
            const h = Math.max(6, Math.round((b.projected_calls / maxCalls) * 60));
            return (
              <div key={b.date} className="flex flex-col items-center gap-1" data-testid={`forecast-bar-${b.date}`}>
                <div className="h-16 w-full flex items-end">
                  <div className="w-full rounded-t-md bg-gradient-to-t from-violet-500 to-violet-300" style={{ height: `${h}px` }} />
                </div>
                <p className="text-[10px] font-semibold text-slate-700 tabular-nums">{b.projected_calls}</p>
                <p className="text-[9px] text-slate-400">{b.date.slice(5)}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Anomalies ───────────────────────────────────────────────────────────────
const AnomalyList = ({ findings }) => {
  if (!findings?.length) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/40 shadow-sm" data-testid="manager-anomalies-clear">
        <CardContent className="pt-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-emerald-800">All clear</p>
            <p className="text-[11px] text-emerald-700">No anomalies detected in the last window.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-slate-200 shadow-sm" data-testid="manager-anomalies">
      <CardContent className="pt-5">
        <h3 className="text-slate-900 font-bold text-sm mb-3 inline-flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-amber-500" /> Anomaly Alerts
          <span className="text-[11px] text-slate-500 font-normal ml-1">({findings.length})</span>
        </h3>
        <ul className="space-y-2">
          {findings.map((f, i) => {
            const s = SEV_STYLE[f.severity] || SEV_STYLE.medium;
            return (
              <li key={`${f.category}-${f.agent_id || i}`}
                  className={`rounded-xl border p-3 flex items-start gap-3 ${s.bg} ${s.border}`}
                  data-testid={`anomaly-${f.category}-${f.agent_id || i}`}>
                <div className={`w-8 h-8 rounded-lg ${s.iconBg} flex items-center justify-center shrink-0`}>
                  <AlertTriangle className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold ${s.text}`}>{f.title}</p>
                  <p className="text-xs text-slate-600 leading-snug mt-0.5">{f.description}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.bg} ${s.text} border ${s.border}`}>
                  {f.severity}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

// ── Top Movers side-by-side ─────────────────────────────────────────────────
const MoversPanel = ({ up, down }) => (
  <Card className="border-slate-200 shadow-sm" data-testid="manager-movers">
    <CardContent className="pt-5">
      <h3 className="text-slate-900 font-bold text-sm mb-3">Top Movers · this week</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600 mb-2 inline-flex items-center gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Up
          </p>
          {up.length === 0 ? <p className="text-xs text-slate-400 italic">no significant risers</p> : (
            <ul className="space-y-1.5">
              {up.map(m => (
                <li key={m.agent_id} className="text-xs" data-testid={`mover-up-${m.agent_id}`}>
                  <span className="text-slate-700 font-semibold">{m.agent_name}</span>
                  <span className="text-emerald-600 font-bold ml-1.5 tabular-nums">+{m.delta}</span>
                  <span className="text-slate-400"> ({m.current})</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-600 mb-2 inline-flex items-center gap-1">
            <TrendingDown className="w-3.5 h-3.5" /> Down
          </p>
          {down.length === 0 ? <p className="text-xs text-slate-400 italic">no significant fallers</p> : (
            <ul className="space-y-1.5">
              {down.map(m => (
                <li key={m.agent_id} className="text-xs" data-testid={`mover-down-${m.agent_id}`}>
                  <span className="text-slate-700 font-semibold">{m.agent_name}</span>
                  <span className="text-rose-600 font-bold ml-1.5 tabular-nums">{m.delta}</span>
                  <span className="text-slate-400"> ({m.current})</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// ── Main tab ─────────────────────────────────────────────────────────────────
/**
 * <TeamPulseTab /> — Area 1 Manager Sales Intelligence.
 * Renders KPIs, top movers, forecast, leaderboard and anomalies in one glance.
 */
const TeamPulseTab = ({ domainId = null }) => {
  const { token } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7); // int preset or 'from:to' custom range

  const load = useCallback(async () => {
    setLoading(true);
    const q = domainId ? `?domain_id=${domainId}` : '';
    const dq = `${q ? '&' : '?'}days=${encodeURIComponent(String(days || 7))}`;
    const anomDays = typeof days === 'number' ? Math.max(3, Math.min(days, 30)) : 7;
    try {
      const [p, f, a] = await Promise.all([
        axios.get(`${API}/manager/team-pulse${q}${dq}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/manager/forecast${q}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/manager/anomalies${q}${q ? '&' : '?'}days=${anomDays}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPulse(p.data); setForecast(f.data); setAnomalies(a.data?.findings || []);
    } catch {
      setPulse(null);
    } finally { setLoading(false); }
  }, [domainId, token, days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>;
  }

  if (!pulse) {
    return (
      <Card className="border-dashed border-slate-200">
        <CardContent className="py-12 text-center">
          <p className="text-slate-500 text-sm">Team pulse is unavailable — try again or check backend logs.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-pulse-tab">
      <DateRangePills days={days} onChange={(v) => setDays(v || 7)} />
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Team size"           value={pulse.team_size}          testid="kpi-team-size" />
        <Kpi label={`Calls (${formatDaysLabel(days).replace('last ', '')})`} value={pulse.calls_this_period}  delta={pulse.calls_delta_pct} testid="kpi-calls" />
        <Kpi label="Team avg score"      value={pulse.team_avg_score}     delta={pulse.team_avg_delta} deltaSuffix=" pts" testid="kpi-avg-score" />
        <Kpi label="Revivable pipeline"  value={pulse.revivable_open_count} testid="kpi-revivable" help="open revivable deals" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Kudos this week"     value={pulse.kudos_this_period}  delta={pulse.kudos_delta_pct} testid="kpi-kudos" />
        <Kpi label="Reflections"         value={pulse.reflections_this_period} testid="kpi-reflections" help="agent self-notes" />
      </div>

      {/* Movers + Forecast side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MoversPanel up={pulse.top_movers_up || []} down={pulse.top_movers_down || []} />
        <ForecastCard forecast={forecast} />
      </div>

      {/* Leaderboard + Anomalies */}
      <Leaderboard domainId={domainId} />

      {/* Chase Kings — chase-win leaderboard */}
      <ChaseKingsCard domainId={domainId} />

      {/* Frequent Collaborators — silo detection from Email-This-Call
          audit trail. Per-user; each manager sees only their own sends. */}
      <FrequentCollaboratorsCard days={90} />

      <AnomalyList findings={anomalies} />
    </div>
  );
};

export default TeamPulseTab;
